import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Calendar, ChevronLeft, ChevronRight, Edit2, Loader, Plus, Save, X } from 'lucide-react';
import api from '../services/api';

const emptySummary = {
  today_total_recovery: 0,
  today_amount_received: 0,
  today_pending: 0,
  today_extra: 0,
  month_total_recovery: 0,
  month_amount_received: 0,
  month_pending_generated: 0,
  previous_pending_total: 0,
  previous_months_breakdown: [],
  pending_received_total: 0,
  extra_adjusted_total: 0,
  total_pending: 0,
  unapplied_extra: 0,
};

// Hardcoded pre-app opening balances (before app tracking started)
const HARDCODED_OPENINGS = [
  { month: '2026-04', label: 'April 2026', amount: 64420 },
  { month: '2026-05', label: 'May 2026', amount: 51100 },
];

const formatMoney = (value) => `PKR ${Number(value || 0).toLocaleString()}`;
const toDateInput = (date) => date.toLocaleDateString('en-CA');
const monthParam = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;

const DailyRecovery = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [ledger, setLedger] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [date, setDate] = useState(toDateInput(new Date()));
  const [totalRecovery, setTotalRecovery] = useState('');
  const [amountReceived, setAmountReceived] = useState('');
  const [notes, setNotes] = useState('');
  const [officerRows, setOfficerRows] = useState([]);
  const [officers, setOfficers] = useState([]);

  const [receiptDate, setReceiptDate] = useState(toDateInput(new Date()));
  const [receiptAmount, setReceiptAmount] = useState('');
  const [receiptTarget, setReceiptTarget] = useState('');
  const [receiptOfficer, setReceiptOfficer] = useState('');
  const [receiptNotes, setReceiptNotes] = useState('');
  const [officerSummary, setOfficerSummary] = useState([]);

  const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });


  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const month = monthParam(currentMonth);

      // Fetch all pages of daily recovery entries
      let allEntries = [];
      let nextPage = `/daily-recovery/?month=${month}`;
      while (nextPage) {
        const res = await api.get(nextPage);
        allEntries = [...allEntries, ...(res.data.results || [])];
        nextPage = res.data.next ? res.data.next.replace(api.defaults.baseURL, '') : null;
      }

      const [summaryRes, ledgerRes, openingsRes, receiptsRes, officerSummaryRes] = await Promise.all([
        api.get('/daily-recovery/summary/', { params: { month } }),
        api.get('/daily-recovery/pending-ledger/', { params: { month } }),
        api.get('/pending-recovery-openings/'),
        api.get('/pending-recovery-receipts/', { params: { month } }),
        api.get('/daily-recovery/officer-summary/', { params: { month } }),
      ]);
      setEntries(allEntries);
      setSummary({ ...emptySummary, ...summaryRes.data });
      setLedger(ledgerRes.data.results || []);
      setOpenings(openingsRes.data.results || openingsRes.data || []);
      setReceipts(receiptsRes.data.results || receiptsRes.data || []);
      setOfficerSummary(officerSummaryRes.data.results || []);
    } catch (err) {
      setError('Failed to load recovery records.');
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    api.get('/officers/').then(res => setOfficers(res.data.results || res.data || [])).catch(() => {});
  }, []);

  const resetDailyForm = () => {
    setEditingId(null);
    setDate(toDateInput(new Date()));
    setTotalRecovery('');
    setAmountReceived('');
    setNotes('');
    setOfficerRows([]);
  };

  const handleDailySubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      date,
      total_recovery: parseFloat(totalRecovery) || 0,
      amount_received: parseFloat(amountReceived) || 0,
      notes: notes.trim(),
    };
    try {
      let savedEntry;
      if (editingId) {
        const res = await api.patch(`/daily-recovery/${editingId}/`, payload);
        savedEntry = res.data;
      } else {
        const res = await api.post('/daily-recovery/', payload);
        savedEntry = res.data;
      }

      // Sync officer pendings: delete all existing, then re-create
      const existingRes = await api.get('/recovery-officer-pendings/', { params: { recovery: savedEntry.id } });
      const existing = existingRes.data.results || existingRes.data || [];
      await Promise.all(existing.map(op => api.delete(`/recovery-officer-pendings/${op.id}/`)));
      const validRows = officerRows.filter(r => r.officer_id && parseFloat(r.amount) > 0);
      await Promise.all(validRows.map(r => api.post('/recovery-officer-pendings/', {
        recovery: savedEntry.id,
        officer: r.officer_id,
        amount: parseFloat(r.amount),
        notes: r.notes || '',
      })));

      resetDailyForm();
      loadData();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save daily recovery.');
    } finally {
      setSaving(false);
    }
  };

  const editEntry = (entry) => {
    setEditingId(entry.id);
    setDate(entry.date);
    setTotalRecovery(entry.total_recovery?.toString() || '');
    setAmountReceived(entry.amount_received?.toString() || '');
    setNotes(entry.notes || '');
    setOfficerRows((entry.officer_pendings || []).map(op => ({
      officer_id: op.officer.toString(),
      amount: op.amount?.toString() || '',
      notes: op.notes || '',
    })));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/pending-recovery-receipts/', {
        date: receiptDate,
        amount: parseFloat(receiptAmount) || 0,
        target_date: receiptTarget || null,
        officer: receiptOfficer || null,
        notes: receiptNotes.trim(),
      });
      setReceiptAmount('');
      setReceiptTarget('');
      setReceiptOfficer('');
      setReceiptNotes('');
      loadData();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save pending received amount.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Daily Total Recovery</h1>
          <p className="text-sm text-gray-500">Track office recovery, received cash flow and pending balances</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-3 py-2">
          <button onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))} className="p-1 hover:bg-gray-100 rounded-lg">
            <ChevronLeft size={18} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-2 px-3 py-1 min-w-[160px] justify-center">
            <Calendar size={16} className="text-blue-600" />
            <span className="text-sm font-semibold text-gray-800">{monthLabel}</span>
          </div>
          <button onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))} className="p-1 hover:bg-gray-100 rounded-lg">
            <ChevronRight size={18} className="text-gray-600" />
          </button>
        </div>
      </div>

      {error && <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <SummaryCard label="Today Total" value={formatMoney(summary.today_total_recovery)} />
        <SummaryCard label="Today Received" value={formatMoney(summary.today_amount_received)} valueClass="text-green-700" />
        <SummaryCard label="Today Pending" value={formatMoney(summary.today_pending)} valueClass="text-red-700" />
        <SummaryCard label="Previous Pendings" value={formatMoney(HARDCODED_OPENINGS.reduce((s, h) => s + h.amount, 0) + summary.previous_pending_total)} valueClass="text-purple-700" />
        <SummaryCard label="Total Outstanding" value={formatMoney(summary.total_pending)} valueClass="text-orange-700" />
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-sm text-white col-span-2 sm:col-span-1">
          <p className="text-xs text-blue-100">Extra Adjusted</p>
          <p className="text-xl font-bold">{formatMoney(summary.extra_adjusted_total)}</p>
        </div>
      </div>

      <form onSubmit={handleDailySubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Plus size={18} className="text-blue-600" />
          {editingId ? 'Edit Daily Recovery' : 'New Daily Recovery'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Date">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-base text-sm bg-white" required />
          </Field>
          <Field label="Total Recovery (PKR)">
            <input type="number" step="1" value={totalRecovery} onChange={(e) => setTotalRecovery(e.target.value)} placeholder="e.g. 10000" className="input-base text-sm bg-white" />
          </Field>
          <Field label="Amount Received (PKR)">
            <input type="number" step="1" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} placeholder="e.g. 7000" className="input-base text-sm bg-white" />
          </Field>
          <Field label="Notes">
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes" className="input-base text-sm bg-white" />
          </Field>
        </div>
        {Math.max(0, parseFloat(totalRecovery || 0) - parseFloat(amountReceived || 0)) > 0 && (
          <div className="mt-5 border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-700">Officer Pending Breakdown</p>
                <p className="text-xs text-gray-400">
                  Total pending: {formatMoney(Math.max(0, parseFloat(totalRecovery || 0) - parseFloat(amountReceived || 0)))}
                  {officerRows.some(r => parseFloat(r.amount) > 0) && (
                    <span className="ml-2 text-orange-600">· Assigned: {formatMoney(officerRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))}</span>
                  )}
                </p>
              </div>
              <button type="button" onClick={() => setOfficerRows(prev => [...prev, { officer_id: '', amount: '', notes: '' }])}
                className="flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium">
                <Plus size={12} /> Add Officer
              </button>
            </div>
            {officerRows.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No officer breakdown added. Click "Add Officer" to split the pending.</p>
            ) : (
              <div className="space-y-2">
                {officerRows.map((row, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <select value={row.officer_id}
                      onChange={e => setOfficerRows(prev => prev.map((r, i) => i === idx ? { ...r, officer_id: e.target.value } : r))}
                      className="input-base text-sm bg-white flex-1">
                      <option value="">Select officer</option>
                      {officers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <input type="number" step="1" value={row.amount} placeholder="Amount"
                      onChange={e => setOfficerRows(prev => prev.map((r, i) => i === idx ? { ...r, amount: e.target.value } : r))}
                      className="input-base text-sm bg-white w-32" />
                    <button type="button" onClick={() => setOfficerRows(prev => prev.filter((_, i) => i !== idx))}
                      className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg flex-shrink-0">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="mt-4 flex gap-2 justify-end">
          {editingId && (
            <button type="button" onClick={resetDailyForm} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
              Cancel
            </button>
          )}
          <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {editingId ? 'Update Recovery' : 'Save Recovery'}
          </button>
        </div>
      </form>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h2 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
            <BarChart3 size={18} className="text-purple-600" />
            Previous Pending Breakdown
          </h2>
          <p className="text-xs text-gray-500 mb-3">Outstanding carried forward into {monthLabel}.</p>
          <div className="space-y-2">
            {HARDCODED_OPENINGS.map(entry => (
              <div key={entry.month} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-800">{entry.label}</p>
                  <p className="text-xs text-gray-400">Pre-app opening balance</p>
                </div>
                <span className="text-sm font-bold text-gray-700">{formatMoney(entry.amount)}</span>
              </div>
            ))}
            {(summary.previous_months_breakdown || [])
              .filter(m => !HARDCODED_OPENINGS.find(h => h.month === m.month))
              .filter(m => m.outstanding > 0)
              .map(m => (
                <div key={m.month} className="flex items-center justify-between border border-blue-100 rounded-lg px-3 py-2 bg-blue-50">
                  <div>
                    <p className="text-sm font-medium text-blue-800">{m.label}</p>
                    <p className="text-xs text-blue-500">Auto-calculated closing pending</p>
                  </div>
                  <span className="text-sm font-bold text-blue-700">{formatMoney(m.outstanding)}</span>
                </div>
              ))}
            <div className="flex items-center justify-between border border-purple-200 rounded-lg px-3 py-2 bg-purple-50 mt-1">
              <div>
                <p className="text-sm font-semibold text-purple-800">Total Previous Pending</p>
              </div>
              <span className="text-sm font-bold text-purple-700">
                {formatMoney(HARDCODED_OPENINGS.reduce((s, h) => s + h.amount, 0) + summary.previous_pending_total)}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleReceiptSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Plus size={18} className="text-green-600" />
            Add Pending Amount Received
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Date">
              <input type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} className="input-base text-sm bg-white" required />
            </Field>
            <Field label="Amount (PKR)">
              <input type="number" step="1" value={receiptAmount} onChange={(e) => setReceiptAmount(e.target.value)} placeholder="e.g. 2000" className="input-base text-sm bg-white" required />
            </Field>
            <Field label="Apply To">
              <select value={receiptTarget} onChange={(e) => setReceiptTarget(e.target.value)} className="input-base text-sm bg-white">
                <option value="">Oldest pending first</option>
                {ledger.filter(row => Number(row.outstanding_amount) > 0).map(row => (
                  <option key={`${row.bucket_type}-${row.bucket_date}`} value={row.bucket_date}>{row.label}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Clear Officer's Pending (optional)">
              <select value={receiptOfficer} onChange={(e) => setReceiptOfficer(e.target.value)} className="input-base text-sm bg-white">
                <option value="">— None / General —</option>
                {officers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </Field>
            <Field label="Notes">
              <input type="text" value={receiptNotes} onChange={(e) => setReceiptNotes(e.target.value)} placeholder="Optional note" className="input-base text-sm bg-white" />
            </Field>
          </div>
          <div className="mt-4 flex justify-end">
            <button disabled={saving} className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium">
              Save Pending Received
            </button>
          </div>
        </form>
      </div>

      <DataTable title={`Date-wise Pending Ledger — ${monthLabel}`} empty="No pending records." columns={['Date/Month', 'Type', 'Original', 'Pending Received', 'Extra Adjusted', 'Remaining']}>
        {ledger.map(row => (
          <tr key={`${row.bucket_type}-${row.bucket_date}`} className="hover:bg-gray-50">
            <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.label}</td>
            <td className="px-4 py-3 text-sm text-gray-600 capitalize">{row.bucket_type}</td>
            <td className="px-4 py-3 text-sm text-right text-gray-900">{formatMoney(row.original_amount)}</td>
            <td className="px-4 py-3 text-sm text-right text-green-700">{formatMoney(row.manual_received_applied)}</td>
            <td className="px-4 py-3 text-sm text-right text-blue-700">{formatMoney(row.extra_adjusted_applied)}</td>
            <td className="px-4 py-3 text-sm text-right font-bold text-orange-700">{formatMoney(row.outstanding_amount)}</td>
          </tr>
        ))}
        {ledger.length > 0 && (
          <tr className="bg-orange-50 border-t-2 border-orange-200">
            <td className="px-4 py-3 text-sm font-bold text-gray-900" colSpan={2}>Total</td>
            <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{formatMoney(ledger.reduce((s, r) => s + Number(r.original_amount), 0))}</td>
            <td className="px-4 py-3 text-sm text-right font-bold text-green-700">{formatMoney(ledger.reduce((s, r) => s + Number(r.manual_received_applied), 0))}</td>
            <td className="px-4 py-3 text-sm text-right font-bold text-blue-700">{formatMoney(ledger.reduce((s, r) => s + Number(r.extra_adjusted_applied), 0))}</td>
            <td className="px-4 py-3 text-sm text-right font-bold text-orange-700">{formatMoney(ledger.reduce((s, r) => s + Number(r.outstanding_amount), 0))}</td>
          </tr>
        )}
      </DataTable>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Monthly Recovery Records ({entries.length})</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader className="animate-spin text-blue-600 mx-auto" size={24} /></div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No recovery records for {monthLabel}</div>
        ) : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Total</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Received</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Pending</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Extra</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Notes</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map(entry => (
                    <React.Fragment key={entry.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{entry.date}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{formatMoney(entry.total_recovery)}</td>
                        <td className="px-4 py-3 text-sm text-right text-green-700">{formatMoney(entry.amount_received)}</td>
                        <td className="px-4 py-3 text-sm text-right text-red-700">{formatMoney(entry.pending_amount)}</td>
                        <td className="px-4 py-3 text-sm text-right text-blue-700">{formatMoney(entry.extra_amount)}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 max-w-[220px] truncate">{entry.notes || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => editEntry(entry)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                            <Edit2 size={14} />
                          </button>
                        </td>
                      </tr>
                      {entry.officer_pendings && entry.officer_pendings.length > 0 && (
                        <tr className="bg-red-50">
                          <td className="px-4 py-1.5 pl-8 text-xs text-gray-500" colSpan={7}>
                            <span className="text-gray-400 mr-2">Pending by officer:</span>
                            {entry.officer_pendings.map(op => (
                              <span key={op.id} className="inline-flex items-center gap-1 mr-4">
                                <span className="font-medium text-gray-700">{op.officer_name}:</span>
                                <span className="text-red-600 font-semibold">{formatMoney(op.amount)}</span>
                              </span>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  <tr className="bg-gray-100 border-t-2 border-gray-300">
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">Total</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{formatMoney(entries.reduce((s, e) => s + Number(e.total_recovery), 0))}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-green-700">{formatMoney(entries.reduce((s, e) => s + Number(e.amount_received), 0))}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-red-700">{formatMoney(entries.reduce((s, e) => s + Number(e.pending_amount), 0))}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-blue-700">{formatMoney(entries.reduce((s, e) => s + Number(e.extra_amount), 0))}</td>
                    <td className="px-4 py-3" colSpan={2}></td>
                  </tr>
                  {officerSummary.length > 0 && (
                    <>
                      <tr className="bg-red-50 dark:bg-red-950/40 border-t-4 border-red-200 dark:border-red-800/60">
                        <td colSpan={7} className="px-4 pt-3 pb-1 text-xs font-bold text-red-700 dark:text-red-300 uppercase tracking-wide">
                          Officer Pending Totals — {monthLabel}
                        </td>
                      </tr>
                      <tr className="bg-red-50 dark:bg-red-950/40 text-xs text-red-800 dark:text-red-300 uppercase">
                        <td className="px-4 py-2 font-semibold" colSpan={2}>Officer</td>
                        <td className="px-4 py-2 text-right font-semibold">Assigned</td>
                        <td className="px-4 py-2 text-right font-semibold">Received</td>
                        <td className="px-4 py-2 text-right font-semibold">Outstanding</td>
                        <td colSpan={2}></td>
                      </tr>
                      {officerSummary.map(os => (
                        <tr key={os.officer_id} className="bg-red-50/60 dark:bg-red-950/20">
                          <td className="px-4 py-2 text-sm font-medium text-gray-800 dark:text-gray-100" colSpan={2}>{os.officer_name || '—'}</td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-gray-100">{formatMoney(os.assigned)}</td>
                          <td className="px-4 py-2 text-sm text-right text-green-700 dark:text-green-400">{formatMoney(os.received)}</td>
                          <td className="px-4 py-2 text-sm text-right font-bold text-red-700 dark:text-red-400">{formatMoney(os.outstanding)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      ))}
                      <tr className="bg-red-100 dark:bg-red-900/40 border-t border-red-200 dark:border-red-800/60">
                        <td className="px-4 py-2 text-sm font-bold text-red-900 dark:text-red-200" colSpan={2}>Officer Totals</td>
                        <td className="px-4 py-2 text-sm text-right font-bold text-gray-900 dark:text-gray-100">
                          {formatMoney(officerSummary.reduce((s, o) => s + Number(o.assigned), 0))}
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-bold text-green-700 dark:text-green-400">
                          {formatMoney(officerSummary.reduce((s, o) => s + Number(o.received), 0))}
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-bold text-red-700 dark:text-red-400">
                          {formatMoney(officerSummary.reduce((s, o) => s + Number(o.outstanding), 0))}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
            <div className="sm:hidden divide-y divide-gray-100">
              {entries.map(entry => (
                <div key={entry.id} className="p-4">
                  <div className="flex justify-between mb-2">
                    <span className="font-bold text-gray-900">{entry.date}</span>
                    <button onClick={() => editEntry(entry)} className="text-xs text-blue-600 font-medium">Edit</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <MobileMetric label="Total" value={formatMoney(entry.total_recovery)} />
                    <MobileMetric label="Received" value={formatMoney(entry.amount_received)} className="text-green-700" />
                    <MobileMetric label="Pending" value={formatMoney(entry.pending_amount)} className="text-red-700" />
                    <MobileMetric label="Extra" value={formatMoney(entry.extra_amount)} className="text-blue-700" />
                  </div>
                  {entry.notes && <p className="text-xs text-gray-500 mt-2">{entry.notes}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {receipts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h2 className="font-semibold text-gray-800 mb-3">Pending Received This Month</h2>
          <div className="divide-y divide-gray-100 text-sm">
            {receipts.map(receipt => (
              <div key={receipt.id} className="flex justify-between py-2">
                <span className="text-gray-700">
                  {receipt.date}
                  {receipt.officer_name && <span className="ml-2 text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">Cleared: {receipt.officer_name}</span>}
                  {receipt.notes ? ` — ${receipt.notes}` : ''}
                </span>
                <span className="font-semibold text-green-700">{formatMoney(receipt.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 border-t-2 border-green-200 mt-1">
              <span className="font-bold text-gray-900">Total Received</span>
              <span className="font-bold text-green-700">{formatMoney(receipts.reduce((s, r) => s + Number(r.amount), 0))}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, valueClass = 'text-gray-900' }) => (
  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
    {children}
  </label>
);

const DataTable = ({ title, empty, columns, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
    <div className="p-4 border-b border-gray-100">
      <h2 className="font-semibold text-gray-800">{title}</h2>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {columns.map((column, index) => (
              <th key={column} className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase ${index > 1 ? 'text-right' : ''}`}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {React.Children.count(children) === 0 ? (
            <tr><td colSpan={columns.length} className="p-8 text-center text-gray-500">{empty}</td></tr>
          ) : children}
        </tbody>
      </table>
    </div>
  </div>
);


const MobileMetric = ({ label, value, className = 'text-gray-900' }) => (
  <div>
    <span className="text-gray-500">{label}:</span> <span className={`font-medium ${className}`}>{value}</span>
  </div>
);

export default DailyRecovery;
